import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useNavigate } from "@tanstack/react-router";

const API_BASE = import.meta.env.VITE_API_URL || "/api";

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? "" : "s"} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  return date.toLocaleDateString();
}

interface DeletionPreview {
  user: { id: string; email: string; name: string };
  organizationsToDelete: { id: string; name: string; domainCount: number; reportCount: number }[];
  membershipsToRemove: number;
}

async function fetchUser(id: string) {
  const res = await fetch(`${API_BASE}/admin/users/${id}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to fetch user");
  return res.json();
}

export function AdminUserDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletionPreview, setDeletionPreview] = useState<DeletionPreview | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-user", id],
    queryFn: () => fetchUser(id),
  });

  const updateUserMutation = useMutation({
    mutationFn: async (updates: { name?: string; emailVerified?: boolean }) => {
      const res = await fetch(`${API_BASE}/admin/users/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error("Failed to update user");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-user", id] });
      setEditingName(false);
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const res = await fetch(`${API_BASE}/admin/members/${memberId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to remove member");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-user", id] });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: async (deactivate: boolean) => {
      const res = await fetch(`${API_BASE}/admin/users/${id}/deactivate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ deactivate }),
      });
      if (!res.ok) throw new Error("Failed to update user status");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-user", id] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_BASE}/admin/users/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to delete user");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      navigate({ to: "/admin/users" });
    },
  });

  const fetchDeletionPreview = async () => {
    const res = await fetch(`${API_BASE}/admin/users/${id}/deletion-preview`, {
      credentials: "include",
    });
    if (!res.ok) throw new Error("Failed to fetch preview");
    const data = await res.json();
    setDeletionPreview(data);
    setShowDeleteModal(true);
  };

  if (isLoading) {
    return <div className="p-8 text-center text-muted-foreground">Loading...</div>;
  }

  if (error || !data) {
    return <div className="p-8 text-center text-red-600">Failed to load user</div>;
  }

  const { user, memberships, activityStats } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="page-header page-header-primary">
        <div className="relative z-10">
          <div className="flex items-center gap-2 text-indigo-200 mb-2">
            <Link to="/admin" className="hover:text-white">Admin</Link>
            <span>/</span>
            <Link to="/admin/users" className="hover:text-white">Users</Link>
            <span>/</span>
            <span>{user.name}</span>
          </div>
          <h1 className="text-3xl font-bold">{user.name}</h1>
          <p className="mt-1 text-indigo-100">{user.email}</p>
        </div>
        <div className="page-header-decor" />
        <div className="page-header-decor-alt" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* User Details */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">User Details</h3>
          <dl className="space-y-4">
            <div className="flex justify-between items-center">
              <dt className="text-muted-foreground">ID</dt>
              <dd className="font-mono text-sm">{user.id}</dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-muted-foreground">Email</dt>
              <dd>{user.email}</dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-muted-foreground">Name</dt>
              <dd>
                {editingName ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      className="input input-primary input-sm w-40"
                    />
                    <button
                      onClick={() => updateUserMutation.mutate({ name: newName })}
                      disabled={updateUserMutation.isPending}
                      className="btn btn-primary btn-sm"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingName(false)}
                      className="btn btn-secondary btn-sm"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <span className="flex items-center gap-2">
                    {user.name}
                    <button
                      onClick={() => {
                        setNewName(user.name);
                        setEditingName(true);
                      }}
                      className="text-indigo-600 hover:text-indigo-500 text-sm"
                    >
                      Edit
                    </button>
                  </span>
                )}
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-muted-foreground">Email Verified</dt>
              <dd className="flex items-center gap-2">
                {user.emailVerified ? (
                  <span className="text-emerald-600 flex items-center gap-1">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Yes
                  </span>
                ) : (
                  <>
                    <span className="text-amber-600">No</span>
                    <button
                      onClick={() => updateUserMutation.mutate({ emailVerified: true })}
                      disabled={updateUserMutation.isPending}
                      className="text-indigo-600 hover:text-indigo-500 text-sm"
                    >
                      Mark Verified
                    </button>
                  </>
                )}
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-muted-foreground">Created</dt>
              <dd>{new Date(user.createdAt).toLocaleString()}</dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-muted-foreground">Updated</dt>
              <dd>{new Date(user.updatedAt).toLocaleString()}</dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-muted-foreground">Last Active</dt>
              <dd>
                {user.lastActiveAt ? (
                  <span className="text-emerald-600">
                    {new Date(user.lastActiveAt).toLocaleString()}
                  </span>
                ) : (
                  <span className="text-muted-foreground">Never</span>
                )}
              </dd>
            </div>
          </dl>
        </div>

        {/* Activity Stats */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Activity</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-lg bg-muted/30 text-center">
              <div className="text-2xl font-bold text-indigo-600">{activityStats?.totalDomains || 0}</div>
              <div className="text-sm text-muted-foreground">Domains</div>
            </div>
            <div className="p-4 rounded-lg bg-muted/30 text-center">
              <div className="text-2xl font-bold text-indigo-600">{activityStats?.totalReports || 0}</div>
              <div className="text-sm text-muted-foreground">Reports</div>
            </div>
            <div className="p-4 rounded-lg bg-muted/30 text-center">
              <div className="text-2xl font-bold text-indigo-600">
                {activityStats?.totalMessages?.toLocaleString() || 0}
              </div>
              <div className="text-sm text-muted-foreground">Emails Processed</div>
            </div>
            <div className="p-4 rounded-lg bg-muted/30 text-center">
              <div className="text-2xl font-bold text-emerald-600">{activityStats?.recentReports || 0}</div>
              <div className="text-sm text-muted-foreground">Reports (7d)</div>
            </div>
          </div>
          {user.lastActiveAt && (
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-sm">
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500"></span>
                <span className="text-muted-foreground">
                  Last seen {formatRelativeTime(user.lastActiveAt)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Organizations */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Organizations ({memberships.length})</h3>
          {memberships.length > 0 ? (
            <div className="space-y-3">
              {memberships.map((membership: any) => (
                <div
                  key={membership.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
                >
                  <div>
                    <Link
                      to={`/admin/organizations/${membership.organization.id}` as any}
                      className="font-medium text-indigo-600 hover:text-indigo-500"
                    >
                      {membership.organization.name}
                    </Link>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`badge ${membership.role === "owner" ? "badge-primary" : "badge-secondary"}`}>
                        {membership.role}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {membership.organization.plan}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      if (confirm("Remove this user from the organization?")) {
                        removeMemberMutation.mutate(membership.id);
                      }
                    }}
                    className="text-red-600 hover:text-red-500 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">User is not a member of any organization.</p>
          )}
        </div>
      </div>

      {/* Danger Zone */}
      <div className="card border-red-200 dark:border-red-800">
        <h3 className="text-lg font-semibold text-red-600 mb-4">Danger Zone</h3>
        <p className="text-sm text-muted-foreground mb-4">
          These actions affect the user's account access and data.
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => deactivateMutation.mutate(!user.deactivatedAt)}
            disabled={deactivateMutation.isPending}
            className={`btn ${user.deactivatedAt ? "btn-primary" : "bg-amber-600 text-white hover:bg-amber-700"}`}
          >
            {deactivateMutation.isPending
              ? "..."
              : user.deactivatedAt
                ? "Reactivate User"
                : "Deactivate User"}
          </button>
          <button
            onClick={fetchDeletionPreview}
            className="btn bg-red-600 text-white hover:bg-red-700"
          >
            Delete User
          </button>
        </div>
        {user.deactivatedAt && (
          <p className="text-sm text-amber-600 mt-3">
            User was deactivated on {new Date(user.deactivatedAt).toLocaleString()}
          </p>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletionPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-red-600 mb-4">Delete User</h3>
            <p className="text-sm text-muted-foreground mb-4">
              You are about to permanently delete <strong>{deletionPreview.user.email}</strong>.
              This action cannot be undone.
            </p>

            {deletionPreview.organizationsToDelete.length > 0 && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <p className="text-sm font-medium text-red-700 dark:text-red-400 mb-2">
                  The following organizations will also be deleted (user is sole member):
                </p>
                <ul className="text-sm space-y-1">
                  {deletionPreview.organizationsToDelete.map((org) => (
                    <li key={org.id} className="text-red-600 dark:text-red-400">
                      <strong>{org.name}</strong>
                      {(org.domainCount > 0 || org.reportCount > 0) && (
                        <span className="text-muted-foreground">
                          {" "}({org.domainCount} domain{org.domainCount !== 1 ? "s" : ""}, {org.reportCount} report{org.reportCount !== 1 ? "s" : ""})
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {deletionPreview.membershipsToRemove > 0 && (
              <p className="text-sm text-muted-foreground mb-4">
                User will be removed from {deletionPreview.membershipsToRemove} organization{deletionPreview.membershipsToRemove !== 1 ? "s" : ""} where other members exist.
              </p>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Type <strong>{deletionPreview.user.email}</strong> to confirm:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="input input-primary w-full"
                placeholder="Enter email to confirm"
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletionPreview(null);
                  setDeleteConfirmText("");
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteConfirmText !== deletionPreview.user.email || deleteMutation.isPending}
                className="btn bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete User Permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
